import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import * as MonitoringIssueActions from "../../../actions/monitoringIssue";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import * as TrialInstitutionActions from "../../../actions/trialInstitution";
import * as PatientActions from "../../../actions/patients";
import { NotificationAlert } from "../../../components/common/index";
import { DatePickerFormInputFabric, SelectFormInput, TextAreaFormInput, TextFormInput } from "../../../components/form/index";
import { RestrictedLayout } from "../../../components/layouts";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { TrialInstitutionMonitoringIssueCreateBreadcrumbs, TrialInstitutionMonitoringIssueDetailsBreadcrumbs } from "../../../helpers/breadcrumbHelper";
import { RegistrationComponent, renderFormControls } from "../../../helpers/formHelper";
import { MonitoringIssueSubMenu } from "../../../helpers/subMenuHelper";
import { useNavigate } from "../../../hooks/useNavigate";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { findLookup, generateOptionsFromLookup } from "src/helpers/lookupHelper";

import "./MonitoringIssuePage.scss";

interface MonitoringIssuePageProps {
    form: Dtos.MonitoringIssue & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    monitoringIssue: Dtos.MonitoringIssue;
    lookups: Dtos.Lookup[];
    loadingMonitoringIssue: boolean;
    loadMonitoringIssueSuccess: boolean;
    loadMonitoringIssueFailure: boolean;
    loadMonitoringIssueErrorCode: string;
    savingMonitoringIssue: boolean;
    saveMonitoringIssueSuccess: boolean;
    saveMonitoringIssueFailure: boolean;
    creatingMonitoringIssue: boolean;
    createMonitoringIssueSuccess: boolean;
    createMonitoringIssueFailure: boolean;
    validatingMonitoringIssue: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    trialInstitution: Dtos.TrialInstitution;
    loadingTrialInstitution: boolean;
    loadTrialInstitutionSuccess: boolean;
    loadTrialInstitutionFailure: boolean;
    loadTrialInstitutionErrorCode: string;
    pageSize: number;
    patients: Dtos.Patient[];
    loadingPatients: boolean;
    loadPatientsSuccess: boolean;
    loadPatientsFailure: boolean;
    loadPatientsErrorCode: string;
    clearMonitoringIssue: MonitoringIssueActions.IMonitoringIssueClearActionCreator;
    loadMonitoringIssue: MonitoringIssueActions.IMonitoringIssueLoadByIdActionCreator;
    saveMonitoringIssue: MonitoringIssueActions.IMonitoringIssueSaveActionCreator;
    createMonitoringIssue: MonitoringIssueActions.IMonitoringIssueCreateActionCreator;
    validateMonitoringIssue: MonitoringIssueActions.IMonitoringIssueValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    clearTrialInstitution: TrialInstitutionActions.ITrialInstitutionClearActionCreator;
    loadTrialInstitution: TrialInstitutionActions.ITrialInstitutionLoadByIdActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
    clearPatients: PatientActions.IPatientClearActionCreator;
    loadPatients: PatientActions.IPatientLoadByTrialInstitutionActionCreator;
}

const reduxFormName: string = "reduxForms.monitoringIssue";

const MonitoringIssuePage: FC<MonitoringIssuePageProps> = ({
    form,
    formState,
    formProperties,
    monitoringIssue,
    lookups,
    loadingMonitoringIssue,
    //loadMonitoringIssueSuccess,
    loadMonitoringIssueFailure,
    loadMonitoringIssueErrorCode,
    savingMonitoringIssue,
    saveMonitoringIssueSuccess,
    saveMonitoringIssueFailure,
    creatingMonitoringIssue,
    //createMonitoringIssueSuccess,
    //createMonitoringIssueFailure,
    validatingMonitoringIssue,
    validationFailures,
    permissions,
    trialInstitution,
    loadingTrialInstitution,
    //loadTrialInstitutionSuccess,
    loadTrialInstitutionFailure,
    loadTrialInstitutionErrorCode,
    patients,
    loadingPatients,
    loadPatientsSuccess,
    loadPatientsFailure,
    loadPatientsErrorCode,
    clearMonitoringIssue,
    loadMonitoringIssue,
    saveMonitoringIssue,
    createMonitoringIssue,
    validateMonitoringIssue,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems,
    clearTrialInstitution,
    loadTrialInstitution,
    clearPatients,
    loadPatients
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const { trialInstitutionId } = useParams<{ trialInstitutionId: string }>();
    const { monitoringId } = useParams<{ monitoringId: string }>();
    const { monitoringIssueId } = useParams<{ monitoringIssueId: string }>();
    const trialIdNumber = parseInt(trialId, 10);
    const trialInstitutionIdNumber = parseInt(trialInstitutionId, 10);
    const monitoringIdNumber = parseInt(monitoringId, 10);
    const monitoringIssueIdNumber = parseInt(monitoringIssueId, 10);

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [formInitalised, setFormInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);
    const [warningShown, setWarningShown] = useState<boolean>(false);
    const [errorShown, setErrorShown] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();

    const loadReduxForm = () => {
        let extraData: Dtos.MonitoringIssue = {} as Dtos.MonitoringIssue;

        loadForm(
            reduxFormName,
            Object.assign({ ...monitoringIssue }, extraData)
        );
    };

    // Load/Create monitoring issue
    useEffect(() => {
        if (monitoringIssueIdNumber && monitoringIdNumber && trialInstitutionIdNumber && 
            trialIdNumber && !monitoringIssue && !loadingMonitoringIssue
        ) {
            setBreadcrumbs(
                TrialInstitutionMonitoringIssueDetailsBreadcrumbs(
                    trialIdNumber, 
                    trialInstitutionIdNumber, 
                    monitoringIdNumber, 
                    monitoringIssueIdNumber
                )
            );

            loadMonitoringIssue(monitoringIssueIdNumber);
        }
        else if (!monitoringIssueIdNumber && monitoringIdNumber && trialInstitutionIdNumber && 
            trialIdNumber && !monitoringIssue && !creatingMonitoringIssue
        ) {
            setDrawerItems([]);
            setBreadcrumbs(
                TrialInstitutionMonitoringIssueCreateBreadcrumbs(
                    trialIdNumber, 
                    trialInstitutionIdNumber, 
                    monitoringIdNumber
                )
            );

            createMonitoringIssue(monitoringIdNumber);
        }
        else if (monitoringIssue && !formInitalised) {
            loadReduxForm();
            setFormInitalised(true);
        }

        if (trialInstitutionIdNumber && !trialInstitution && !loadingTrialInstitution) {
            loadTrialInstitution(trialInstitutionIdNumber);
        }
    }, [
        monitoringIssueIdNumber,
        trialInstitutionIdNumber,
        trialIdNumber,
        monitoringIssue,
        loadingMonitoringIssue,
        creatingMonitoringIssue,
        formInitalised,
        trialInstitution,
        loadingTrialInstitution,
        monitoringIdNumber
    ]);

    // Load Patients
    useEffect(() => {
        if (trialInstitution && !loadingPatients && !loadPatientsSuccess) {
            loadPatients(trialInstitution.trialId, trialInstitution.institutionId);
        }
    }, [trialInstitution, loadingPatients, loadPatientsSuccess]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadMonitoringIssueFailure) {
            navigate(`/error/${loadMonitoringIssueErrorCode}`);
        }
        if (loadTrialInstitutionFailure) {
            navigate(`/error/${loadTrialInstitutionErrorCode}`);
        }
        if (loadPatientsFailure) {
            navigate(`/error/${loadPatientsErrorCode}`);
        }
    }, [
        loadMonitoringIssueFailure,
        loadMonitoringIssueErrorCode,
        loadTrialInstitutionFailure,
        loadTrialInstitutionErrorCode,
        loadPatientsFailure,
        loadPatientsErrorCode
    ]);

    // Update breadcrumbs & drawer items
    useEffect(() => {
        if (monitoringIssueIdNumber && monitoringIdNumber && trialIdNumber && 
            trialInstitutionIdNumber && trialInstitution && permissions
        ) {
            setDrawerItems(
                MonitoringIssueSubMenu(
                    trialIdNumber, 
                    trialInstitutionIdNumber, 
                    monitoringIdNumber, 
                    monitoringIssueIdNumber, 
                    permissions
                )
            );
            setBreadcrumbs(
                TrialInstitutionMonitoringIssueDetailsBreadcrumbs(
                    trialIdNumber,
                    trialInstitutionIdNumber,
                    monitoringIdNumber,
                    monitoringIssueIdNumber,
                    trialInstitution?.trial?.displayName,
                    trialInstitution?.institution?.name
                )
            );
        }
        else if (!monitoringIssueIdNumber && monitoringIdNumber && 
            trialInstitutionIdNumber && trialIdNumber && trialInstitution
        ) {
            setDrawerItems([]);
            setBreadcrumbs(
                TrialInstitutionMonitoringIssueCreateBreadcrumbs(
                    trialIdNumber, 
                    trialInstitutionIdNumber, 
                    monitoringIdNumber,
                    trialInstitution?.trial?.displayName, 
                    trialInstitution?.institution?.name
                )
            );
        }
    }, [
        monitoringIssueIdNumber,
        monitoringIdNumber, 
        trialIdNumber,
        trialInstitutionIdNumber,
        trialInstitution,
        monitoringIssue,
        permissions
    ]);

    // Reload monitoring Issue after saving
    useEffect(() => {
        if (!savingMonitoringIssue && saveMonitoringIssueSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Monitoring Issue saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                navigate(`/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/monitoring/${monitoringIdNumber}/issues`);
            } else if (!monitoringIssueIdNumber) {
                navigate(`/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/monitoring/${monitoringIdNumber}/issues/${monitoringIssue.id}/details`);
                clearMonitoringIssue();
                clearTrialInstitution();
            }
        }
    }, [
        savingMonitoringIssue,
        saveMonitoringIssueSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingMonitoringIssue && saveMonitoringIssueFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingMonitoringIssue, saveMonitoringIssueFailure]);

     // Cleanup effect to clear the monitoring issue & trial institution on component unmount
    useEffect(() => {
        return () => {
            clearMonitoringIssue();
            clearTrialInstitution();
            clearPatients();
            resetForm(reduxFormName);
        };
    }, [
        clearMonitoringIssue, 
        clearTrialInstitution, 
        clearPatients,
        resetForm, 
        reduxFormName
    ]);

    // Warning if no patients are selectable
    useEffect(() => {
        if ((!patients || patients?.length == 0) && loadPatientsSuccess && !warningShown) {
            Alert.warning(
                <NotificationAlert
                    alertContent={<span>The patient list is currently empty. Please add a patient manually if necessary.</span>}
                    icon={FontAwesomeIcons.Solid.EXCLAMATION_TRIANGLE}
                />,
                { timeout: 8000 }
            );
            setWarningShown(true);
        }
    }, [patients, loadPatientsSuccess, warningShown]);

    // If patients is undefined the trial database doesn't have the required view or function. 
    useEffect(() => {
        if (!patients && loadPatientsSuccess && !errorShown) {
            Alert.error(
                <NotificationAlert
                    alertContent={<span>An error has occured trying to fetch the patients list. Please contact support.</span>}
                    icon={FontAwesomeIcons.Solid.EXCLAMATION_TRIANGLE}
                />,
                { timeout: 8000 }
            );
            setErrorShown(true);
        }
    }, [patients, loadPatientsSuccess, errorShown]);

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.MonitoringIssue = {} as Dtos.MonitoringIssue;

            if (!data.monitoringId) {
                changes.monitoringId = monitoringIdNumber;
            }

            await saveMonitoringIssue(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    const handleOnSubmitFailed = (_data: Dtos.MonitoringIssue) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        navigate(`/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/monitoring/${monitoringIdNumber}/issues`);
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.MonitoringIssue = {} as Dtos.MonitoringIssue;

            if (!form.monitoringId) {
                changes.monitoringId = monitoringIdNumber;
            }

            if (form && hasBeenSubmitted && !validatingMonitoringIssue) {
                await validateMonitoringIssue(Object.assign({ ...form }, changes));
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const patientSelectOptions = useMemo(() => {
        if (!patients || patients.length === 0) return [];

        var options = patients
            ?.map((x) => ({ value: x.randNo, label: `${x.randNo} - ${x.init}` }));

        return options?.sort((a, b) => a?.label?.localeCompare(b?.label));
    }, [patients]);

    const renderContent = useMemo(() => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.MonitoringIssueUpdate,
            undefined,
            undefined
        );

        if (hasPermission) {
            const formDisabled: boolean =
                !hasPermission ||
                savingMonitoringIssue ||
                (formState && formState.formStatus == Dtos.FormStatus.Disabled);
            const patientIssueTypeId = findLookup("MonitoringIssue", "monitoringIssueTypeId", lookups)?.values?.find(x => x.value == "Patient Issue")?.id;
            const yesExecutiveInformedId = findLookup("MonitoringIssue", "monitoringIssueExecutiveInformedId", lookups)?.values?.find(x => x.value == "Yes")?.id;
            const yesIssueNotifiedId = findLookup("MonitoringIssue", "monitoringIssueNotifiedId", lookups)?.values?.find(x => x.value == "Yes")?.id;
            const closedIssueStatusId = findLookup("MonitoringIssue", "monitoringIssueStatusId", lookups)?.values?.find(x => x.value == "Closed")?.id;

            let formComponents: { [index: string]: RegistrationFormComponent } =
                {
                    details: {
                        type: RegistrationComponent.RegistrationElement,
                        component: (
                            <>
                                <h1>
                                    Details
                                </h1>
                                <div className="card card-shadow">

                                    <div className="form-row pt-4 pb-2 px-4">
                                        <div className="col-6">
                                            {renderFormControls(
                                                form,
                                                "monitoringIssue",
                                                {
                                                    monitoringIssueTypeId: {
                                                        type: RegistrationComponent.RegistrationControl,
                                                        inputType: SelectFormInput,
                                                        inputDisabled: formDisabled,
                                                        inputProps: {
                                                            options:
                                                                generateOptionsFromLookup(
                                                                    findLookup(
                                                                        "monitoringIssue",
                                                                        "monitoringIssueTypeId",
                                                                        lookups
                                                                    ),
                                                                    form?.monitoringIssueTypeId
                                                                ),
                                                            clearable: true
                                                        },
                                                        changeAction: (_model, value, _form, _parentModel) => {
                                                            changeForm(reduxFormName + ".monitoringIssueTypeId", value);
                                                            if (!value || value != patientIssueTypeId) {
                                                                changeForm(reduxFormName + ".patient", null);
                                                            }
                                                        },
                                                        groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                        labelClassName: "col-form-label col-xxl-4 col-xl-12",
                                                        fieldClassName: "col-xxl-7 col-xl-12 generic-form-field",
                                                    },
                                                },
                                                lookups,
                                                validationFailures,
                                                formProperties,
                                                changeForm
                                            )}
                                        </div>
                                        <div className="col-6">
                                            {renderFormControls(
                                                form,
                                                "monitoringIssue",
                                                {
                                                    patient: {
                                                        type: RegistrationComponent.RegistrationControl,
                                                        inputType: patientSelectOptions?.length > 0 ? SelectFormInput : TextFormInput,
                                                        inputDisabled: formDisabled || form.monitoringIssueTypeId != 2,
                                                        inputProps: {
                                                            options: patientSelectOptions,
                                                            clearable: true,    
                                                            loading: loadingPatients                                                    
                                                        },
                                                        groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                        labelClassName: "col-form-label col-xxl-3 col-xl-12",
                                                        fieldClassName: "col-xxl-9 col-xl-12 generic-form-field",
                                                    },
                                                },
                                                lookups,
                                                validationFailures,
                                                formProperties,
                                                changeForm
                                            )}
                                        </div>
                                    </div>
                                    <div className="form-row py-2 px-4">
                                        <div className="col-6">
                                            {renderFormControls(
                                                form,
                                                "monitoringIssue",
                                                {
                                                    monitoringIssueDeviationTypeId: {
                                                        type: RegistrationComponent.RegistrationControl,
                                                        inputType: SelectFormInput,
                                                        inputDisabled: formDisabled,
                                                        inputProps: {
                                                            options:
                                                                generateOptionsFromLookup(
                                                                    findLookup(
                                                                        "monitoringIssue",
                                                                        "monitoringIssueDeviationTypeId",
                                                                        lookups
                                                                    ),
                                                                    form?.monitoringIssueDeviationTypeId
                                                                ),
                                                            clearable: true
                                                        },
                                                        groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                        labelClassName: "col-form-label col-xxl-4 col-xl-12",
                                                        fieldClassName: "col-xxl-7 col-xl-12 generic-form-field",
                                                    },
                                                },
                                                lookups,
                                                validationFailures,
                                                formProperties,
                                                changeForm
                                            )}
                                        </div>
                                        <div className="col-6">
                                            {renderFormControls(
                                                form,
                                                "monitoringIssue",
                                                {
                                                    monitoringIssueCategoryId: {
                                                        type: RegistrationComponent.RegistrationControl,
                                                        inputType: SelectFormInput,
                                                        inputDisabled: formDisabled,
                                                        inputProps: {
                                                            options:
                                                                generateOptionsFromLookup(
                                                                    findLookup(
                                                                        "monitoringIssue",
                                                                        "monitoringIssueCategoryId",
                                                                        lookups
                                                                    ),
                                                                    form?.monitoringIssueCategoryId
                                                                ),
                                                            clearable: true
                                                        },
                                                        groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                        labelClassName: "col-form-label col-xxl-3 col-xl-12",
                                                        fieldClassName: "col-xxl-9 col-xl-12 generic-form-field",
                                                    },
                                                },
                                                lookups,
                                                validationFailures,
                                                formProperties,
                                                changeForm
                                            )}
                                        </div>
                                    </div>
                                    <div className="form-row py-2 px-4">
                                        <div className="col-12">
                                            {renderFormControls(
                                                form,
                                                "monitoringIssue",
                                                {
                                                    comments: {
                                                        type: RegistrationComponent.RegistrationControl,
                                                        inputType: TextAreaFormInput,
                                                        inputDisabled: formDisabled,
                                                        inputProps: { rows: 3 },
                                                        groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                        labelClassName: "col-form-label col-2",
                                                        fieldClassName: "col-10 generic-form-field",
                                                    },
                                                },
                                                lookups,
                                                validationFailures,
                                                formProperties,
                                                changeForm
                                            )}
                                        </div>
                                    </div>
                                    <div className="form-row py-2 px-4">
                                        <div className="col-12">
                                            {renderFormControls(
                                                form,
                                                "monitoringIssue",
                                                {
                                                    correctiveAction: {
                                                        type: RegistrationComponent.RegistrationControl,
                                                        inputType: TextAreaFormInput,
                                                        inputDisabled: formDisabled,
                                                        inputProps: { rows: 3 },
                                                        groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                        labelClassName: "col-form-label col-2",
                                                        fieldClassName: "col-10 generic-form-field",
                                                    },
                                                },
                                                lookups,
                                                validationFailures,
                                                formProperties,
                                                changeForm
                                            )}
                                        </div>
                                    </div>
                                    <div className="form-row py-2 px-4">
                                        <div className="col-6">
                                            {renderFormControls(
                                                form,
                                                "monitoringIssue",
                                                {
                                                    monitoringIssueExecutiveInformedId: {
                                                        type: RegistrationComponent.RegistrationControl,
                                                        inputType: SelectFormInput,
                                                        inputDisabled: formDisabled,
                                                        inputProps: {
                                                            options:
                                                                generateOptionsFromLookup(
                                                                    findLookup(
                                                                        "monitoringIssue",
                                                                        "monitoringIssueExecutiveInformedId",
                                                                        lookups
                                                                    ),
                                                                    form?.monitoringIssueExecutiveInformedId
                                                                ),
                                                            clearable: true
                                                        },
                                                        changeAction: (_model, value, _form, _parentModel) => {
                                                            changeForm(reduxFormName + ".monitoringIssueExecutiveInformedId", value);
                                                            if (!value || value != yesExecutiveInformedId) {
                                                                changeForm(reduxFormName + ".executiveInformedDate", null);
                                                            }
                                                        },
                                                        groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                        labelClassName: "col-form-label col-xxl-4 col-xl-12",
                                                        fieldClassName: "col-xxl-7 col-xl-12 generic-form-field",
                                                    },
                                                },
                                                lookups,
                                                validationFailures,
                                                formProperties,
                                                changeForm
                                            )}
                                        </div>
                                        <div className="col-6">
                                            {renderFormControls(
                                                form,
                                                "monitoringIssue",
                                                {
                                                    executiveInformedDate: {
                                                        type: RegistrationComponent.RegistrationControl,
                                                        inputType: DatePickerFormInputFabric,
                                                        inputDisabled: formDisabled || form?.monitoringIssueExecutiveInformedId != yesExecutiveInformedId,
                                                        groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                        labelClassName: "col-form-label col-xxl-3 col-xl-12",
                                                        fieldClassName: "col-xxl-9 col-xl-12 generic-form-field",
                                                    },
                                                },
                                                lookups,
                                                validationFailures,
                                                formProperties,
                                                changeForm
                                            )}
                                        </div>
                                    </div>
                                    <div className="form-row py-2 px-4">
                                        <div className="col-12">
                                            {renderFormControls(
                                                form,
                                                "monitoringIssue",
                                                {
                                                    monitoringIssueActionTakenId: {
                                                        type: RegistrationComponent.RegistrationControl,
                                                        inputType: SelectFormInput,
                                                        inputDisabled: formDisabled,
                                                        inputProps: {
                                                            options:
                                                                generateOptionsFromLookup(
                                                                    findLookup(
                                                                        "monitoringIssue",
                                                                        "monitoringIssueActionTakenId",
                                                                        lookups
                                                                    ),
                                                                    form?.monitoringIssueActionTakenId
                                                                ),
                                                            clearable: true
                                                        },
                                                        groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                        labelClassName: "col-form-label col-xxl-2 col-xl-12",
                                                        fieldClassName: "col-xxl-10 col-xl-12 generic-form-field",
                                                    },
                                                },
                                                lookups,
                                                validationFailures,
                                                formProperties,
                                                changeForm
                                            )}
                                        </div>
                                    </div>
                                    <div className="form-row py-2 px-4">
                                        <div className="col-6">
                                            {renderFormControls(
                                                form,
                                                "monitoringIssue",
                                                {
                                                    monitoringIssueNotifiedId: {
                                                        type: RegistrationComponent.RegistrationControl,
                                                        inputType: SelectFormInput,
                                                        inputDisabled: formDisabled,
                                                        inputProps: {
                                                            options:
                                                                generateOptionsFromLookup(
                                                                    findLookup(
                                                                        "monitoringIssue",
                                                                        "monitoringIssueNotifiedId",
                                                                        lookups
                                                                    ),
                                                                    form?.monitoringIssueNotifiedId
                                                                ),
                                                            clearable: true
                                                        },
                                                        changeAction: (_model, value, _form, _parentModel) => {
                                                            changeForm(reduxFormName + ".monitoringIssueNotifiedId", value);
                                                            if (!value || value != yesIssueNotifiedId) {
                                                                changeForm(reduxFormName + ".notifiedDate", null);
                                                            }
                                                        },
                                                        groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                        labelClassName: "col-form-label col-xxl-4 col-xl-12",
                                                        fieldClassName: "col-xxl-7 col-xl-12 generic-form-field",
                                                    },
                                                },
                                                lookups,
                                                validationFailures,
                                                formProperties,
                                                changeForm
                                            )}
                                        </div>
                                        <div className="col-6">
                                            {renderFormControls(
                                                form,
                                                "monitoringIssue",
                                                {
                                                    notifiedDate: {
                                                        type: RegistrationComponent.RegistrationControl,
                                                        inputType: DatePickerFormInputFabric,
                                                        inputDisabled: formDisabled || form?.monitoringIssueNotifiedId != yesIssueNotifiedId,
                                                        groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                        labelClassName: "col-form-label col-xxl-3 col-xl-12",
                                                        fieldClassName: "col-xxl-9 col-xl-12 generic-form-field",
                                                    },
                                                },
                                                lookups,
                                                validationFailures,
                                                formProperties,
                                                changeForm
                                            )}
                                        </div>
                                    </div>
                                    <div className="form-row pt-2 pb-4 px-4">
                                        <div className="col-6">
                                            {renderFormControls(
                                                form,
                                                "monitoringIssue",
                                                {
                                                    monitoringIssueStatusId: {
                                                        type: RegistrationComponent.RegistrationControl,
                                                        inputType: SelectFormInput,
                                                        inputDisabled: formDisabled,
                                                        inputProps: {
                                                            options:
                                                                generateOptionsFromLookup(
                                                                    findLookup(
                                                                        "monitoringIssue",
                                                                        "monitoringIssueStatusId",
                                                                        lookups
                                                                    ),
                                                                    form?.monitoringIssueStatusId
                                                                ),
                                                            clearable: true
                                                        },
                                                        changeAction: (_model, value, _form, _parentModel) => {
                                                            changeForm(reduxFormName + ".monitoringIssueStatusId", value);
                                                            if (!value || value != closedIssueStatusId) {
                                                                changeForm(reduxFormName + ".closedDate", null);
                                                            }
                                                        },
                                                        groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                        labelClassName: "col-form-label col-xxl-4 col-xl-12",
                                                        fieldClassName: "col-xxl-7 col-xl-12 generic-form-field",
                                                    },
                                                },
                                                lookups,
                                                validationFailures,
                                                formProperties,
                                                changeForm
                                            )}
                                        </div>
                                        <div className="col-6">
                                            {renderFormControls(
                                                form,
                                                "monitoringIssue",
                                                {
                                                    closedDate: {
                                                        type: RegistrationComponent.RegistrationControl,
                                                        inputType: DatePickerFormInputFabric,
                                                        inputDisabled: formDisabled || form?.monitoringIssueStatusId != closedIssueStatusId,
                                                        groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                        labelClassName: "col-form-label col-xxl-3 col-xl-12",
                                                        fieldClassName: "col-xxl-9 col-xl-12 generic-form-field",
                                                    },
                                                },
                                                lookups,
                                                validationFailures,
                                                formProperties,
                                                changeForm
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        ),
                    },
                };

            return (
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "monitoringIssue",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(reduxFormName + ".saveAndReturn", false);
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(reduxFormName + ".saveAndReturn", true);
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            );
        }
        return null;
    }, [
        permissions,
        lookups,
        form,
        permissions,
        onCancel,
        handleOnSubmit,
        handleOnSubmitFailed,
        loadingMonitoringIssue,
        savingMonitoringIssue,
        creatingMonitoringIssue,
        formState,
        monitoringIssue,
        patientSelectOptions
    ]);

    return (
        <RestrictedLayout
            loading={
                loadingMonitoringIssue || 
                creatingMonitoringIssue ||
                loadingTrialInstitution
            }
            permission={Dtos.Permission.MonitoringIssueView}
        >
            { renderContent }
        </RestrictedLayout>
    )
};

const mapStateToProps = (state: ITmdState): Partial<MonitoringIssuePageProps> => {
    let monitoringIssue: Dtos.MonitoringIssue | undefined = !(state.monitoringIssue.formData instanceof Array)
        ? state.monitoringIssue.formData
        : undefined;
    let trialInstitution: Dtos.TrialInstitution | undefined = !(state.trialInstitution.formData instanceof Array)
        ? state.trialInstitution.formData
        : undefined;
    let patients: Dtos.Patient[] | undefined = (state.patients.data instanceof Array)
        ? state.patients.data
        : undefined;
    let permissions = state.user.userSummary ? state.user.userSummary.permissions : [];

    return {
        monitoringIssue: monitoringIssue!,
        loadingMonitoringIssue: state.monitoringIssue.loadState && state.monitoringIssue.loadState.status == RequestState.Pending,
        loadMonitoringIssueSuccess: state.monitoringIssue.loadState && state.monitoringIssue.loadState.status == RequestState.Success,
        loadMonitoringIssueFailure: state.monitoringIssue.loadState && state.monitoringIssue.loadState.status == RequestState.Failure,
        loadMonitoringIssueErrorCode: state.monitoringIssue.loadState.errorCode,
        savingMonitoringIssue: state.monitoringIssue.saveState && state.monitoringIssue.saveState.status == RequestState.Pending,
        saveMonitoringIssueSuccess: state.monitoringIssue.saveState && state.monitoringIssue.saveState.status == RequestState.Success,
        saveMonitoringIssueFailure: state.monitoringIssue.saveState && state.monitoringIssue.saveState.status == RequestState.Failure,
        creatingMonitoringIssue: state.monitoringIssue.createState && state.monitoringIssue.createState.status == RequestState.Pending,
        createMonitoringIssueSuccess: state.monitoringIssue.createState && state.monitoringIssue.createState.status == RequestState.Success,
        createMonitoringIssueFailure: state.monitoringIssue.createState && state.monitoringIssue.createState.status == RequestState.Failure,
        validatingMonitoringIssue: state.monitoringIssue.validateState && state.monitoringIssue.validateState.status == RequestState.Pending,
        permissions: permissions,
        lookups: state.monitoringIssue.lookups,
        form: state.reduxForms.monitoringIssue,
        formState: state.monitoringIssue.formState,
        formProperties: state.monitoringIssue.formProperties,
        validationFailures: monitoringIssue
            ? state.monitoringIssue.validationFailures
            : undefined,
        trialInstitution: trialInstitution!,
        loadingTrialInstitution: state.trialInstitution.loadState && state.trialInstitution.loadState.status == RequestState.Pending,
        loadTrialInstitutionSuccess: state.trialInstitution.loadState && state.trialInstitution.loadState.status == RequestState.Success,
        loadTrialInstitutionFailure: state.trialInstitution.loadState && state.trialInstitution.loadState.status == RequestState.Failure,
        loadTrialInstitutionErrorCode: state.trialInstitution.loadState.errorCode,
        patients: patients!,
        loadingPatients: state.patients.loadState && state.patients.loadState.status == RequestState.Pending,
        loadPatientsSuccess: state.patients.loadState && state.patients.loadState.status == RequestState.Success,
        loadPatientsFailure: state.patients.loadState && state.patients.loadState.status == RequestState.Failure,
        loadPatientsErrorCode: state.patients.loadState.errorCode,
    };
};

const mapDispatchToProps = (dispatch): Partial<MonitoringIssuePageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadMonitoringIssue: bindActionCreators(MonitoringIssueActions.LoadMonitoringIssueById, dispatch),
        clearMonitoringIssue: bindActionCreators(MonitoringIssueActions.Clear, dispatch),
        saveMonitoringIssue: bindActionCreators(MonitoringIssueActions.SaveMonitoringIssue, dispatch),
        createMonitoringIssue: bindActionCreators(MonitoringIssueActions.CreateMonitoringIssue, dispatch),
        validateMonitoringIssue: bindActionCreators(MonitoringIssueActions.ValidateMonitoringIssue, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch),
        loadTrialInstitution: bindActionCreators(TrialInstitutionActions.LoadTrialInstitutionById, dispatch),
        clearTrialInstitution: bindActionCreators(TrialInstitutionActions.Clear, dispatch),
        loadPatients: bindActionCreators(PatientActions.LoadPatientByTrialInstitution, dispatch),
        clearPatients: bindActionCreators(PatientActions.Clear, dispatch),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(MonitoringIssuePage);